import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FiThumbsUp, FiThumbsDown, FiBookmark } from "react-icons/fi";
import { X, Loader2, ChevronDown } from "lucide-react";
import FormattedMessage from "./FormattedMessage";

const useRelatedContent = ({ sources, defaultOpen, isStreaming }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [userToggled, setUserToggled] = useState(false);

  useEffect(() => {
    if (!userToggled) {
      setIsOpen(defaultOpen);
    }
  }, [defaultOpen, userToggled]);

  useEffect(() => {
    if (isStreaming) {
      setIsOpen(false);
      setUserToggled(false);
    }
  }, [isStreaming]);

  const toggle = useCallback(() => {
    setIsOpen((v) => !v);
    setUserToggled(true);
  }, []);

  return { isOpen, toggle };
};

const RelatedContentToggle = ({ count, isOpen, toggle }) => {
  if (!count) return null;

  return (
    <button
      onClick={toggle}
      className="flex items-center gap-1 text-[12px] font-light text-mutedText hover:text-mainText transition-colors ml-1 cursor-pointer"
    >
      <ChevronDown
        className={[
          "h-3.5 w-3.5 transition-transform duration-200",
          isOpen ? "" : "-rotate-90",
        ].join(" ")}
      />
      <span>
        {count} extra source{count !== 1 ? "s" : ""}
      </span>
    </button>
  );
};

const RelatedContentPanel = ({
  sources,
  isOpen,
  onSourceClick,
  activeSourceId,
  citationInteractions,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (isOpen && panelRef.current) {
      requestAnimationFrame(() => {
        panelRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    }
  }, [isOpen]);

  if (!isOpen || !sources || sources.length === 0) return null;

  return (
    <div ref={panelRef} className="flex flex-wrap gap-1.5 pb-1 max-w-[720px]">
      {sources.map((src, i) => {
        const clean = (src.title || "")
          .replace(/[^\x20-\x7E\u00C0-\u024F]/g, "")
          .trim();
        const label = clean
          ? clean.length > 30
            ? clean.slice(0, 29) + "…"
            : clean
          : `Source ${src.index}`;
        const interactions = citationInteractions?.[src.doc_id];
        return (
          <button
            key={src.doc_id || i}
            title={clean || `Source ${src.index}`}
            onClick={() => onSourceClick?.(src)}
            className={[
              "inline-flex items-center gap-1 h-[22px] px-2 rounded text-[11px] font-light leading-none whitespace-nowrap cursor-pointer transition-colors",
              activeSourceId && activeSourceId === src.doc_id
                ? "bg-velBlue/15 text-velBlue ring-1 ring-velBlue/30"
                : "bg-surface/80 text-mutedText border border-inputBorder hover:bg-btnHover",
            ].join(" ")}
          >
            {label}
            {interactions &&
              (interactions.like ||
                interactions.dislike ||
                interactions.catalog) && (
                <span className="inline-flex items-center gap-px">
                  {interactions.like && (
                    <span className="inline-block w-[7px] h-[7px] rounded-full bg-activeCase" />
                  )}
                  {interactions.dislike && (
                    <span className="inline-block w-[7px] h-[7px] rounded-full bg-logoutColor" />
                  )}
                  {interactions.catalog && (
                    <span className="inline-block w-[7px] h-[7px] rounded-full bg-sunColor" />
                  )}
                </span>
              )}
          </button>
        );
      })}
    </div>
  );
};

const AnswerBubble = ({
  entry,
  onToggleLike,
  onToggleDislike,
  onToggleCatalog,
  onOpenSidebar,
  onRetry,
  onSourceClick,
  isRightOpen,
  isNewest,
  isStreaming,
  activeSourceId,
  citationInteractions,
  onOpenInEditor,
}) => {
  const usedIndices = useMemo(() => {
    if (!entry.answer) return new Set();
    const indices = new Set();
    const re = /\[(\d+)\]/g;
    let match;
    while ((match = re.exec(entry.answer)) !== null) {
      indices.add(parseInt(match[1], 10));
    }
    return indices;
  }, [entry.answer]);

  const extraSources = useMemo(() => {
    if (!entry.relatedSources || entry.relatedSources.length === 0) return [];

    const seen = new Set();
    const deduped = entry.relatedSources.filter((s) => {
      const key = s.doc_id || s.url || s.title || JSON.stringify(s);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (usedIndices.size === 0) return deduped;
    return deduped.filter((s) => !usedIndices.has(s.index));
  }, [entry.relatedSources, usedIndices]);

  const hasRelated = extraSources.length > 0;
  const related = useRelatedContent({
    sources: extraSources,
    defaultOpen: false,
    isStreaming,
  });

  const hasActive =
    entry.like || entry.dislike || entry.catalog || related.isOpen;

  if (entry.isError) {
    return (
      <div className="group/msg flex flex-col gap-1.5">
        <div className="max-w-[720px] flex items-start gap-2.5 rounded-lg border border-logoutColor/30 bg-logoutColor/10 px-3.5 py-3">
          <X className="h-4 w-4 text-logoutColor shrink-0 mt-0.5" />
          <div className="flex flex-col gap-2">
            <p className="text-[13px] font-light leading-relaxed text-logoutColor">
              {entry.answer}
            </p>
            {entry.retryPayload && onRetry && (
              <button
                onClick={() => onRetry(entry)}
                className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-md text-[12px] font-light border border-logoutColor/40 bg-logoutColor/10 text-logoutColor hover:bg-logoutColor/20 transition-colors cursor-pointer"
              >
                <Loader2 className="h-3 w-3" style={{ animation: "none" }} />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group/msg flex flex-col gap-1">
      <div className="max-w-[720px]">
        <div className="text-[14px] font-light leading-relaxed text-mainText">
          <FormattedMessage
            text={entry.answer}
            citations={entry.citations}
            onSourceClick={onSourceClick}
            activeSourceId={activeSourceId}
            citationInteractions={citationInteractions}
            onOpenInEditor={onOpenInEditor}
          />
        </div>
      </div>

      <div
        className={[
          "flex items-center gap-3 pt-2 pb-2 transition-opacity duration-150",
          hasActive ? "opacity-100" : "opacity-0 group-hover/qa:opacity-100",
        ].join(" ")}
      >
        <button
          onClick={() => onToggleLike(entry)}
          className="transition-colors cursor-pointer"
        >
          <FiThumbsUp
            className={
              entry.like
                ? "h-[15px] w-[15px] text-activeCase"
                : "h-[15px] w-[15px] text-mutedText/40 hover:text-mutedText"
            }
          />
        </button>

        <button
          onClick={() => onToggleDislike(entry)}
          className="transition-colors cursor-pointer"
        >
          <FiThumbsDown
            className={
              entry.dislike
                ? "h-[15px] w-[15px] text-logoutColor"
                : "h-[15px] w-[15px] text-mutedText/40 hover:text-mutedText"
            }
          />
        </button>

        <button
          onClick={() => onToggleCatalog(entry)}
          className="transition-colors cursor-pointer"
        >
          <FiBookmark
            className={
              entry.catalog
                ? "h-[15px] w-[15px] text-sunColor"
                : "h-[15px] w-[15px] text-mutedText/40 hover:text-mutedText"
            }
          />
        </button>

        {hasRelated && (
          <RelatedContentToggle
            count={extraSources.length}
            isOpen={related.isOpen}
            toggle={related.toggle}
          />
        )}
      </div>

      {hasRelated && (
        <RelatedContentPanel
          sources={extraSources}
          isOpen={related.isOpen}
          onSourceClick={onSourceClick}
          activeSourceId={activeSourceId}
          citationInteractions={citationInteractions}
        />
      )}
    </div>
  );
};

export default AnswerBubble;
